import {useCallback} from 'react'
import BasketMgr from '../custom-api/basket-custom-api'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useMultiSite from './use-multi-site'
import {useCurrentBasket} from '@app/hooks/use-current-basket'
import useShipToCountry from '@app/hooks/use-ship-to-country'

import {getLocalStorageJSONItem} from '../utils/utils'

/**
 * Custom React hook for managing and accessing basket assignments within the application.
 * Typically used to retrieve, update, or manipulate assignment data related to the user's shopping basket.
 * @returns {Function} - A function to manually trigger basket assignment.
 */
const useBasketAssignments = ({refetchBasket} = {}) => {
    const {getTokenWhenReady} = useAccessToken()
    const {site, locale} = useMultiSite()
    const {shipToCountry} = useShipToCountry()

    const countryCodeStillSetting = getLocalStorageJSONItem('countryCode')

    const assignBasket = useCallback(
        async (
            isCartPage = false,
            toRefetch = true,
            resetAssignments = false,
            countryCodeOverride = null
        ) => {
            const countryCode = countryCodeOverride || shipToCountry?.id

            if (!countryCode || (!countryCodeOverride && countryCodeStillSetting)) {
                return false
            }

            try {
                const accessToken = await getTokenWhenReady()
                const basketMgr = new BasketMgr({
                    parameters: {
                        siteId: site.id,
                        locale: locale.id
                    }
                })

                await basketMgr.sfsBasketAssignments(
                    accessToken,
                    site.id,
                    locale.id,
                    isCartPage,
                    countryCode,
                    resetAssignments
                )

                if (toRefetch && refetchBasket) {
                    await refetchBasket()
                }

                return true
            } catch (error) {
                console.error('Error assigning basket:', error)
                return false
            }
        },
        [
            getTokenWhenReady,
            site.id,
            locale.id,
            shipToCountry?.id,
            countryCodeStillSetting,
            refetchBasket
        ]
    )

    return assignBasket
}

export default useBasketAssignments
